import { useEffect, useState, type ReactNode } from "react";
import { useAuth } from "../../auth/AuthProvider.js";
import {
  Badge,
  Card,
  DataTable,
  type Column
} from "../../components/index.js";
import type { BoxTracking, Shipment } from "../../types.js";
import { trackingStatusBadge } from "../../lib/tracking-status.js";
import type { StepProps } from "./step-props.js";

/**
 * Etapa 5 — Detalhes de rastreamento (Design.md §6.5). Mensagem de sucesso +
 * tabela de rastreamento por caixa (reusa GET /:id/shipments da PR-2).
 *
 * Limitação documentada (gap de backend): §6.5 lista também Etiqueta FBA (FNSKU),
 * Peso e Dimensões por caixa, mas o `BoxTracking` do backend (getShipment →
 * spdTrackingItems) só expõe boxId + trackingId + status de validação. Essas
 * colunas só entram quando o DTO for estendido (fora do escopo deste polimento).
 */
export function Step5Rastreamento({ planId, onReadyChange }: StepProps): ReactNode {
  const { api } = useAuth();
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    onReadyChange(true);
  }, [onReadyChange]);

  useEffect(() => {
    let cancelled = false;
    api
      .getPlanShipments(planId)
      .then((res) => {
        if (!cancelled) setShipments(res.data.shipments);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setCarregando(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, planId]);

  const columns: Column<BoxTracking>[] = [
    { key: "box", header: "Nº da caixa", render: (b) => <span className="mono">{b.boxId}</span> },
    {
      key: "tracking",
      header: "Número de rastreamento",
      render: (b) =>
        b.trackingId !== null ? (
          <span className="mono">{b.trackingId}</span>
        ) : (
          <span style={{ color: "var(--text-faint)" }}>—</span>
        )
    },
    {
      key: "status",
      header: "Status",
      render: (b) => {
        const badge = trackingStatusBadge(b.status);
        return badge !== null ? (
          <Badge tone={badge.tone}>{badge.label}</Badge>
        ) : (
          <span style={{ color: "var(--text-faint)" }}>—</span>
        );
      }
    }
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--green)" }}>
        ✓ Seus envios estão concluídos.
      </div>
      {carregando ? <p>Carregando rastreamento…</p> : null}
      {!carregando && shipments.length === 0 ? (
        <Card>
          <p style={{ color: "var(--text-muted)" }}>
            Nenhum shipment com rastreamento ainda.
          </p>
        </Card>
      ) : null}
      {shipments.map((shipment) => (
        <Card key={shipment.shipmentId} title={shipment.name ?? shipment.shipmentId}>
          <div className="mono" style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: "0.5rem" }}>
            {shipment.shipmentId}
            {shipment.destinationFcCode !== null ? ` · ${shipment.destinationFcCode}` : ""}
          </div>
          <DataTable
            columns={columns}
            rows={shipment.boxTracking}
            getRowKey={(b) => b.boxId}
            empty="Sem caixas rastreadas."
          />
        </Card>
      ))}
    </div>
  );
}
